import {
  useId,
  useMemo,
  useState,
  type CSSProperties,
  type FocusEventHandler,
} from 'react';
import {
  AsYouType,
  getCountries,
  getCountryCallingCode,
  type CountryCode,
} from 'libphonenumber-js';
import { IconCheck, IconChevronDown, IconSearch } from '@tabler/icons-react';
import { cn } from '../tokens/cn';
import { focusRing, transitionBase } from '../tokens/variants';
import { Popover, PopoverContent, PopoverTrigger } from '../primitives/popover';

export interface PhoneInputChange {
  /** Raw national digits, no spaces, no dial code, no plus. e.g. "3001234567" */
  national: string;
  /** Full E.164 format with leading "+". e.g. "+573001234567" */
  e164: string;
  /** Lowercase ISO 3166-1 alpha-2 country code. e.g. "co" */
  countryCode: string;
  /** Dial code without "+". e.g. "57" */
  dialCode: string;
  /** Pretty formatted national string from AsYouType. e.g. "300 123 4567" */
  formatted: string;
}

export interface PhoneInputProps {
  id?: string;
  name?: string;
  /** Raw national digits (no dial code). */
  value?: string;
  onChange?: (next: PhoneInputChange) => void;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  defaultCountry?: CountryCode;
  preferredCountries?: CountryCode[];
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  containerStyle?: CSSProperties;
  /** Locale used to localize country names via `Intl.DisplayNames`. Defaults to "en". */
  locale?: string;
  searchPlaceholder?: string;
}

const heightBySize = {
  sm: 'h-8',
  md: 'h-10',
  lg: 'h-12',
};

const fontBySize = {
  sm: 'text-sm',
  md: 'text-sm',
  lg: 'text-base',
};

interface CountryEntry {
  code: CountryCode;
  name: string;
  dial: string;
  flag: string;
}

const codeToFlag = (code: string) =>
  code
    .toUpperCase()
    .replace(/[A-Z]/g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));

const getDisplayName = (code: CountryCode, locale: string): string => {
  try {
    const display = new Intl.DisplayNames([locale], { type: 'region' });
    return display.of(code) ?? code;
  } catch {
    return code;
  }
};

export const PhoneInput = ({
  id,
  name,
  value = '',
  onChange,
  onBlur,
  defaultCountry = 'CO',
  preferredCountries = ['CO', 'MX', 'US', 'BR'],
  placeholder = 'Phone number',
  disabled,
  invalid,
  size = 'md',
  className,
  containerStyle,
  locale = 'en',
  searchPlaceholder = 'Search country',
}: PhoneInputProps) => {
  const reactId = useId();
  const inputId = id ?? reactId;
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [country, setCountry] = useState<CountryCode>(defaultCountry);

  const countries = useMemo<CountryEntry[]>(() => {
    const all = getCountries().map<CountryEntry>((c) => ({
      code: c,
      name: getDisplayName(c, locale),
      dial: getCountryCallingCode(c),
      flag: codeToFlag(c),
    }));
    all.sort((a, b) => a.name.localeCompare(b.name, locale));
    const preferred = preferredCountries
      .map((p) => all.find((a) => a.code === p))
      .filter((x): x is CountryEntry => Boolean(x));
    const rest = all.filter((a) => !preferredCountries.includes(a.code));
    return [...preferred, ...rest];
  }, [locale, preferredCountries]);

  const current = countries.find((c) => c.code === country) ?? countries[0];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return countries;
    const numeric = q.replace(/^\+/, '');
    return countries.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.code.toLowerCase().includes(q) ||
        (numeric && c.dial.startsWith(numeric)),
    );
  }, [search, countries]);

  const formattedValue = useMemo(() => {
    const raw = value.replace(/\D/g, '');
    if (!raw) return '';
    return new AsYouType(country).input(raw);
  }, [value, country]);

  const emit = (rawDigits: string, nextCountry: CountryCode) => {
    const dial = getCountryCallingCode(nextCountry);
    const formatted = rawDigits ? new AsYouType(nextCountry).input(rawDigits) : '';
    onChange?.({
      national: rawDigits,
      e164: rawDigits ? `+${dial}${rawDigits}` : '',
      countryCode: nextCountry.toLowerCase(),
      dialCode: dial,
      formatted,
    });
  };

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '');
    emit(raw, country);
  };

  const handleSelectCountry = (next: CountryCode) => {
    setCountry(next);
    setOpen(false);
    setSearch('');
    emit(value.replace(/\D/g, ''), next);
  };

  const heightClass = heightBySize[size];
  const fontClass = fontBySize[size];

  if (!current) return null;

  return (
    <div className={cn('flex w-full', className)} style={containerStyle}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            aria-label={`Country: ${current.name}. Click to change.`}
            className={cn(
              'inline-flex items-center gap-1.5 px-2.5 shrink-0',
              'bg-[var(--tk-surface-bg-elevated)] text-[var(--tk-surface-fg)]',
              'border border-[var(--tk-surface-border-strong)] border-r-0',
              'rounded-l-tk-md',
              'hover:bg-[var(--tk-surface-bg)]',
              'disabled:opacity-60 disabled:cursor-not-allowed',
              focusRing,
              transitionBase,
              heightClass,
              fontClass,
            )}
          >
            <span className="tk-flag text-base leading-none">{current.flag}</span>
            <span className="text-[var(--tk-surface-fg-muted)] tabular-nums">
              +{current.dial}
            </span>
            <IconChevronDown
              size={14}
              className={cn(
                'text-[var(--tk-surface-fg-muted)] transition-transform',
                open && 'rotate-180',
              )}
            />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={6}
          className="w-72 p-0 overflow-hidden"
        >
          <div className="border-b border-[var(--tk-surface-border)] p-2">
            <div className="relative">
              <IconSearch
                size={14}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--tk-surface-fg-muted)]"
              />
              <input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                className={cn(
                  'w-full h-8 pl-8 pr-3 text-sm',
                  'bg-[var(--tk-surface-bg)] text-[var(--tk-surface-fg)]',
                  'placeholder:text-[var(--tk-surface-fg-subtle)]',
                  'border border-[var(--tk-surface-border-strong)] rounded-tk-sm',
                  focusRing,
                  transitionBase,
                )}
              />
            </div>
          </div>
          <div
            role="listbox"
            aria-label="Country list"
            className="max-h-72 overflow-y-auto p-1"
          >
            {filtered.length === 0 && (
              <div className="px-3 py-6 text-sm text-[var(--tk-surface-fg-muted)] text-center">
                No countries found
              </div>
            )}
            {filtered.map((c) => {
              const selected = c.code === country;
              return (
                <button
                  key={c.code}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => handleSelectCountry(c.code)}
                  className={cn(
                    'w-full flex items-center gap-2.5 px-2.5 py-1.5 text-sm rounded-tk-sm',
                    'text-[var(--tk-surface-fg)] cursor-pointer text-left',
                    'hover:bg-[var(--tk-brand-bg-subtle)]',
                    selected &&
                      'bg-[var(--tk-brand-bg-subtle)] text-[var(--tk-brand-fg-on-subtle)] font-medium',
                  )}
                >
                  <span className="tk-flag text-base leading-none shrink-0">{c.flag}</span>
                  <span className="flex-1 truncate">{c.name}</span>
                  <span
                    className={cn(
                      'text-xs tabular-nums shrink-0',
                      selected
                        ? 'text-[var(--tk-brand-fg-on-subtle)]'
                        : 'text-[var(--tk-surface-fg-muted)]',
                    )}
                  >
                    +{c.dial}
                  </span>
                  {selected && (
                    <IconCheck
                      size={14}
                      className="shrink-0 text-[var(--tk-brand-fg-on-subtle)]"
                    />
                  )}
                </button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
      <input
        id={inputId}
        name={name}
        type="tel"
        inputMode="tel"
        autoComplete="tel-national"
        value={formattedValue}
        onChange={handleInput}
        onBlur={onBlur}
        placeholder={placeholder}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        className={cn(
          'flex-1 min-w-0 px-3',
          'bg-[var(--tk-surface-bg-elevated)] text-[var(--tk-surface-fg)]',
          'placeholder:text-[var(--tk-surface-fg-subtle)]',
          'border border-[var(--tk-surface-border-strong)] rounded-r-tk-md',
          focusRing,
          transitionBase,
          'disabled:opacity-60 disabled:cursor-not-allowed',
          'aria-[invalid=true]:border-[var(--tk-danger-bg)] aria-[invalid=true]:focus-visible:ring-[var(--tk-danger-bg)]',
          heightClass,
          fontClass,
        )}
      />
    </div>
  );
};
